import * as Sentry from '@sentry/react';
import { scrubIfUrl, scrubUrl } from './scrubUrl';

/** Снять с события всё, что говорит, ЧЬЯ это ошибка.
 *
 *  `sendDefaultPii: false` останавливает только то, что библиотека
 *  добавляет сама. Явный `Sentry.setUser({ email })` — одна строка, и
 *  почта родителя уезжает мимо настройки целиком; узнать об этом
 *  неоткуда, экран здоров и событий не прибавляется.
 *
 *  Тест запрещает такую строку в исходниках, но запрет по тексту
 *  обходится: `setUser` можно взять в переменную, поле можно присвоить
 *  через `Object.assign`, вызов может прийти из чужого кода. Поэтому
 *  снятие стоит ЗДЕСЬ, на выходе: что бы кто ни выставил, наружу это не
 *  уйдёт. Запрет в исходниках при этом остаётся — он ловит намерение
 *  раньше и на виду у ревью.
 *
 *  Граница честная: два эти обработчика видят события об ошибках и следы
 *  производительности. Повтор сессии уходит своим конвертом и через них
 *  не проходит — там защита прежняя, `setUser` просто не вызывается. */
function забыть(event: { user?: unknown }): void {
  delete event.user;
}

/** Ключи, под которыми библиотека держит строку запроса или фрагмент
 *  отдельно от адреса: `http.query`, `url.query`, `http.fragment`. */
const КЛЮЧ_ЧАСТИ_АДРЕСА = /query|fragment/i;

/** Значение — сама строка запроса или фрагмент, без адреса вокруг. */
const ЗНАЧЕНИЕ_ЧАСТИ_АДРЕСА = /^[?#]/;

/** Почистить поля `data` отрезка, следа или хлебной крошки.
 *
 *  Мерка — по СМЫСЛУ значения, а не по перечню ключей. Перечень здесь уже
 *  стоял (`url`, `http.url`, `server.address`) и отстал от библиотеки:
 *  Sentry 10.x кладёт адрес ещё в `url.full`, строку запроса — в
 *  `http.query`, фрагмент — в `http.fragment`, и с каждым десятым запуском
 *  боя адреса PostgREST с идентификатором ребёнка уходили наружу целиком.
 *  Следующее обновление назовёт поле иначе, поэтому смотрим на само
 *  значение: похожее на адрес — через `scrubUrl`; строка запроса или
 *  фрагмент без адреса вокруг, и всё под ключом с `query`/`fragment` в
 *  имени, — долой целиком.
 *
 *  Вглубь вложенных объектов обход не идёт: в `data` хлебной крошки
 *  консоли лежат аргументы `console.log` — живые объекты приложения, и
 *  менять их на месте нельзя. Массив строк заменяется новым массивом. */
function чиститьДанные(data: Record<string, unknown> | undefined): void {
  if (!data) return;
  for (const key of Object.keys(data)) {
    const value = data[key];
    if (КЛЮЧ_ЧАСТИ_АДРЕСА.test(key) ||
        (typeof value === 'string' && ЗНАЧЕНИЕ_ЧАСТИ_АДРЕСА.test(value))) {
      delete data[key];
    } else if (typeof value === 'string') {
      data[key] = scrubIfUrl(value);
    } else if (Array.isArray(value)) {
      data[key] = value.map(scrubIfUrl);
    }
  }
}

/** Крошка: поля `data` и текст, если он сам является адресом.
 *
 *  Навигация и консоль кладут адрес в `message`, не в `data`. Фраза,
 *  которая адресом не является, остаётся. Адрес в середине фразы сюда не
 *  попадает: `scrubIfUrl` смотрит только на строку целиком. */
function чиститьКрошку(crumb: { data?: Record<string, unknown>; message?: unknown }): void {
  чиститьДанные(crumb.data);
  if (typeof crumb.message === 'string') crumb.message = scrubIfUrl(crumb.message);
}

/** Общее для события об ошибке и следа: адрес запроса, крошки, след. */
function чиститьСобытие(event: {
  request?: { url?: string };
  breadcrumbs?: Array<{ data?: Record<string, unknown>; message?: unknown }>;
  contexts?: { trace?: { data?: Record<string, unknown> } };
}): void {
  if (event.request?.url) event.request.url = scrubUrl(event.request.url);
  for (const crumb of event.breadcrumbs ?? []) чиститьКрошку(crumb);
  чиститьДанные(event.contexts?.trace?.data);
}

/** Сбор ошибок включается только когда задан адрес приёма. Без него
 *  приложение работает как обычно, просто молча. */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    /** Среда, а не режим сборки. `MODE` у боевой, у дев-выкладки И у
     *  локальной сборки одинаков («production»), и без отдельной метки ошибки
     *  с дева смешались бы с настоящими: разбирая происшествие у ребёнка,
     *  разработчик копался бы в собственных экспериментах. Выкладка всегда
     *  задаёт `VITE_ENVIRONMENT` явно (`scripts/deploy.sh`), значит сборка без
     *  неё — чья-то локальная: так пришла MULT-TRAINER-8 с localhost:4173. */
    environment: import.meta.env.VITE_ENVIRONMENT ?? 'local',
    /** Доля трассировки. На стенде берём всё, в боевой сборке — десятую
     *  часть: с появлением учётных записей трафик становится настоящим, а
     *  каждый след — это ещё один адрес запроса к базе, который надо было
     *  почистить. Меньше следов — меньше поверхности. */
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1,
    integrations: [
      Sentry.browserTracingIntegration(),
      /** Маскировка задана ЯВНО, а не оставлена на умолчания библиотеки.
       *
       *  Сегодня умолчания верные — текст и поля маскируются, медиа
       *  блокируется. Но повтор сессии пишет ролик вокруг ошибки, а ошибки
       *  случаются и на форме входа, где родитель набирает почту и пароль.
       *  Цена перевёрнутого умолчания при обновлении библиотеки — пароли
       *  родителей в чужом сервисе, и узнать об этом будет неоткуда: экран
       *  здоров, событий нет, ролик просто содержит больше, чем должен.
       *
       *  Умолчание, на которое опираются молча, — это не решение, а
       *  надежда. Три строки ниже превращают надежду в решение, и тест
       *  требует именно их, а не отсутствия `false`. */
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    // В журнал ошибок не должно попадать ничего, кроме техники.
    // `dataCollection` рядом НЕ задавать: с ним Sentry 10.75 игнорирует эту
    // строку и берёт разрешающие умолчания — IP, куки, заголовки, тела
    // (измерено; сторожит tests/sentry.test.ts).
    sendDefaultPii: false,

    /** Три рубежа чистки адресов. Их три, а не один, потому что адрес
     *  попадает наружу тремя разными дорогами: хлебной крошкой навигации
     *  или запроса, полем `request.url` события и адресом внутри следа
     *  производительности. Забыть любую — значит не сделать ничего. Поля
     *  `data` на каждой дороге чистятся по смыслу значения, а не по
     *  перечню ключей (`чиститьДанные`). */
    beforeBreadcrumb(crumb) {
      чиститьКрошку(crumb);
      return crumb;
    },

    beforeSend(event) {
      забыть(event);
      чиститьСобытие(event);
      return event;
    },

    beforeSendTransaction(event) {
      забыть(event);
      чиститьСобытие(event);
      event.transaction = scrubIfUrl(event.transaction) as string | undefined;
      for (const span of event.spans ?? []) {
        span.description = scrubIfUrl(span.description) as string | undefined;
        чиститьДанные(span.data as Record<string, unknown> | undefined);
      }
      return event;
    },
  });
}

export const ErrorBoundary = Sentry.ErrorBoundary;

/** Отправить отказ, который приложение обработало само.
 *
 *  Нужна отдельно от `ErrorBoundary`: тот ловит ТОЛЬКО исключения
 *  отрисовки. Необработанные отклонения обещаний ловит не он, а интеграция
 *  глобальных обработчиков — она включена по умолчанию библиотеки, и
 *  опираться на неё молча значит связывать обещание на экране с настройкой,
 *  которую никто не выбирал. А перехваченный отказ (чтение хранилища,
 *  запись занятия) не доходит никуда вообще: его поймали, значит он
 *  необработанным не станет. Экран при этом обещает «мы уже знаем об этой ошибке» —
 *  и обещание должно быть правдой, иначе родитель решит не писать, и никто
 *  никогда не узнает, что приложение для этой семьи сломано.
 *
 *  Без адреса приёма молчит, как и весь сбор.
 *
 *  `level` по умолчанию `'error'` — прежнее и подавляющее большинство мест
 *  зовут `report` без третьего довода, и их смысл не должен был поменяться
 *  ни на одну букву. Уровень нужен ровно одному месту на сегодня —
 *  `registerServiceWorker`, где отказ РЕГИСТРАЦИИ бывает и нашей поломкой
 *  выкладки (`error`), и решением браузера, к которому мы отношения не
 *  имеем (`warning`; раздел «Сбор ошибок» в `AGENTS.md`).
 *
 *  `'info'` добавлен для `переоткрытие:успех` (`connectionGuard.ts`):
 *  единственное место, где `report` называет не поломку, а её ПОЧИНКУ —
 *  подтверждение, что переоткрытие соединения после обрыва сработало на
 *  живом устройстве. */
export function report(
  error: unknown,
  where: string,
  level: 'error' | 'warning' | 'info' = 'error',
): void {
  Sentry.captureException(error, { tags: { where }, level });
}

/** След КАЖДОГО переоткрытия соединения с IndexedDB — не событие само по
 *  себе (это была бы отдельная запись на каждый обрыв, а обрывы у Safari в
 *  фоне не редкость), а хлебная крошка, которая ляжет ВНУТРЬ следующего
 *  настоящего события, если до него дойдёт. `имяОтказа` — `.name` причины
 *  (`connectionGuard.ts`), а не текст: тексты у обрывов произвольные, а имя
 *  — из закрытого списка и одинаково для одного и того же механизма что в
 *  проде, что в тесте. */
export function следПереоткрытия(имяОтказа: string): void {
  Sentry.addBreadcrumb({ category: 'db-reconnect', message: имяОтказа, level: 'info' });
}

/** Обещание, чей отказ НЕ разбирают по существу в месте вызова: либо оно
 *  доказанно не отказывает — чтением, с номером строки, в комментарии у
 *  вызова, — либо отказ ему не портит экран (фоновое, само показывает своё
 *  состояние). НЕ для мест, где отказ оставляет экран в негодном
 *  состоянии — занятость не снимается, кнопка замирает, слова не
 *  появляются: там чинят по существу, а не гасят здесь.
 *
 *  Отказ уходит в Sentry ВСЕГДА — с `place` под этим именем, без него под
 *  `отпустить`. Молча его не снимать: «доказанно не отказывает» — довод
 *  чтением, и если однажды он перестанет быть верным, это обязано быть
 *  слышно. Прежний `void p` был громким — необработанное отклонение ловит
 *  глобальный перехватчик Sentry, — и тихая помощница была бы шагом назад
 *  (измерено 2026-09-25: первая редакция без `place` глотала отказ).
 *  Задвоения это не даёт: места без `place` — вызываемые, которые сами всё
 *  ловят и не отклоняются вовсе, так что до `report` здесь дело не доходит.
 *
 *  Довод типа — `void | Promise<unknown>`, а не только `Promise`: у
 *  `NavigateFunction` react-router именно такая сигнатура (обычный роутер
 *  навигацию не откладывает и возвращает `void`, роутер данных — обещание),
 *  и без союза каждый вызов `navigate(...)` пришлось бы приводить типом. */
export function отпустить(promise: void | Promise<unknown>, place?: string): void {
  if (!promise) return;
  promise.catch((error: unknown) => {
    report(error, place ?? 'отпустить');
  });
}
